import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled, { css } from "styled-components";

import { Strings } from "--root/strings/en";

import { ListEntry } from "--floponents-web/atoms/ListEntry";
import { TextInput } from "--floponents-web/atoms/TextInput";
import { scrollbarCSS } from "--floponents-web/common/scrollbarCss";
import { ListSurface } from "--floponents-web/molecules/ListEntries/ListSurface/ListSurface";

const StyledPicker = styled.div``;
const PickerFilterInput = styled(TextInput)`
    padding: ${props => props.theme.spacing(1, 2)};
`;

const PickerList = styled.div<{ mobile?: boolean }>`
    ${props => props.mobile && css`
        width: 100vw;
    `}
    max-height: min(50vh, 300px);
    overflow: auto;
    ${scrollbarCSS()};
`;

export interface PickerProps<T> {
    /** Items to display. */
    items: T[];
    /** List of recent items. */
    recents?: T[];
    /** Whether to allow multiple items to be picked. */
    allowMultiple?: boolean;
    /** Should show recents list. */
    showRecents?: boolean;
    /** Should show filter UI. */
    showFilter?: boolean;
    /** Should wrap items in list entry components. */
    wrapListEntries?: boolean;

    /** FC to wrap the list in defaults to PickerList */
    listWrapper?: React.FC

    /** Called when list key is needed for item. */
    keyFor: (item: T) => string;
    /**
     * Called when item needs to be rendered. If `allowMultiple` is `true`, a
     * second boolean argument is passed representing the selected state.
     */
    renderFor: (item: T, isSelected?: boolean) => React.ReactElement;
    /** Called when item needs to be filtered. */
    filterFor?: (item: T, filterTerm: string) => boolean;

    /** Called when item is picked. */
    onPick?: (item: T) => void;
    /** Called when multiple item selection is changed. */
    onSelectionChanged?: (items: T[]) => void;
    /** Called when recent items are updated (after pick). */
    onRecentsUpdated?: (recents: T[]) => void;
    /* Called when filter is changed. */
    onFilterTermChanged?: (filterTerm: string) => void;

    className?: string;
}

/** Generic picker list component. */
export const Picker = <T,>(props: PickerProps<T>) => {
    const {
        onFilterTermChanged,
        onPick,
        onSelectionChanged,
        onRecentsUpdated,
        filterFor,
        wrapListEntries,
        className,
        listWrapper
    } = props;

    const [filterTerm, setFilterTerm] = useState("");
    const handleFilterInputChange = useCallback((
        ev: React.ChangeEvent<HTMLInputElement>
    ) => {
        const newFilterTerm = ev.target.value;
        setFilterTerm(newFilterTerm);
    }, []);

    const [selectedItems, setSelectedItems] = useState(new Set<T>());

    useEffect(() => {
        onFilterTermChanged?.(filterTerm);
    }, [filterTerm, onFilterTermChanged]);

    useEffect(() => {
        onSelectionChanged?.(Array.from(selectedItems));
    }, [onSelectionChanged, selectedItems]);

    const filtered = useMemo(() => (
        // If filter is set and item filter callback was provided
        filterTerm && filterFor
            ? props.items.filter(item => filterFor(item, filterTerm))
            : props.items
    ), [filterFor, filterTerm, props.items]);

    const recentsMerged = useMemo(() => {
        if (
            // Not showing recents or no recents to show
            (!props.showRecents || !props.recents) ||
            // Filter term set
            filterTerm
        ) {
            return filtered;
        }

        // Merged-deduped recents/items arrays 
        return [...new Set([...props.recents, ...props.items])];
    }, [filterTerm, filtered, props.items, props.recents, props.showRecents]);

    const handleSelectedItem = useCallback((index: number) => {
        const item = recentsMerged[index];
        if (!item) return;

        if (props.allowMultiple) {
            setSelectedItems(items => {
                const newItems = new Set(items);
                if (newItems.has(item)) {
                    newItems.delete(item);
                } else {
                    newItems.add(item);
                }
                return newItems;
            });
        } else {
            onPick?.(item);
        }

        // Handle recent items update (if enabled)
        if (props.showRecents) {
            let newRecents: T[] = [];

            // If no recent items, use new recent items array
            if (!props.recents) {
                newRecents = [item];
            } else if (
                // Less than five recent items
                props.recents.length < 5 ||
                // ... or picked item is already a recent item
                props.recents.some(recent => (
                    props.keyFor(recent) === props.keyFor(item)
                ))
            ) {
                // Add picked item to start and de-dupe
                newRecents = [...new Set([item, ...props.recents])];
            } else {
                // Add picked item to start, de-dupe and pop last item
                newRecents = [...new Set([item, ...props.recents.slice(0, -1)])];
            }

            onRecentsUpdated?.(newRecents);
        }

        // Clear filter
        if (props.showFilter && !props.allowMultiple) setFilterTerm("");
    }, [onPick, onRecentsUpdated, props, recentsMerged]);

    const Wrapper = listWrapper || PickerList;

    return <StyledPicker className={className}>
        {props.showFilter && (
            <PickerFilterInput
                placeholder={Strings.search.postfilterTip(0)}
                value={filterTerm}
                onChange={handleFilterInputChange}
            />
        )}

        <Wrapper>
            <ListSurface
                allowClickThrough
                key={filterTerm}
                dividerPosition={!filterTerm ? props.recents?.length : undefined}
                onSelectedItem={handleSelectedItem} >
                {recentsMerged.map(item => {
                    const renderedItem = props.renderFor(
                        item,
                        props.allowMultiple
                            ? selectedItems.has(item)
                            : undefined
                    );

                    return wrapListEntries
                        ? <ListEntry key={props.keyFor(item)} indentStyle="dropdownMenuDense">{renderedItem}</ListEntry>
                        : renderedItem
                })}
            </ListSurface>
        </Wrapper>
    </StyledPicker>;
};
